import {
  Paper,
  Grid,
  Typography,
  Box,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import '../../scss/components/mobile-payments.scss';
import { formatNumberToUSD } from 'src/shared/calculators';
import { usePaymentProgramView } from './use-payment-program-view';
import { FullPaymentView } from './full-payment-view';
import { PreauthorizedPaymentView } from './preauthorized-payment-view';
import { OptimalPaymentView } from './optimal-payment-view';
import { CustomProgramView } from './custom-program-view';
import { CustomResultsView } from './custom-results-view';
import { HreApprovalView } from './hre-approval-view';
import { buildCustomResultsNavigationState } from './saved-program-prefill';

interface PaymentProgramViewProps {
  isMobile?: boolean;
}

const PaymentProgramView = ({ isMobile = false }: PaymentProgramViewProps) => {
  const ctx = usePaymentProgramView(isMobile);
  const {
    isMobile: isMobileCtx,
    currentProgramType,
    programType,
    pfrAmt,
    pfrCollected,
    remainingPfr,
    patientId,
    encounterId,
    getProgramPath,
    activeEncounter,
    showDownPayment,
    showRecurringAmount,
    billingFrequency,
    setBillingFrequency,
  } = ctx;

  const showBillingFrequencyToggle =
    currentProgramType === 'optimal' || currentProgramType === 'custom';

  const renderCustomView = () => {
    if (programType === 'hre-approval') {
      return <HreApprovalView ctx={ctx} />;
    }
    if (ctx.isCustomResults) {
      return <CustomResultsView ctx={ctx} />;
    }
    return <CustomProgramView ctx={ctx} />;
  };

  return (
    <main>
      <div className="content px-4 py-4">
        <Paper elevation={2} sx={{ p: 4, maxWidth: 950, mx: 'auto' }}>
          <Typography variant="h5" component="h1" align="center" gutterBottom fontWeight="bold" sx={{ mb: 2 }}>
            {isMobileCtx ? 'Mobile Program' : 'Payment Program'}
          </Typography>

          {/* Navigation tabs (desktop only) */}
          {!isMobileCtx && (
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center', width: '100%' }} test-id="payment-program-type-tabs">
              <ToggleButtonGroup
                value={currentProgramType}
                test-id="payment-program-type-buttongroup"
                exclusive
                onChange={(event, newValue) => {
                  if (newValue !== null) {
                    if (newValue === 'custom' && (showDownPayment || showRecurringAmount)) {
                      ctx.navigate(
                        getProgramPath(`/hub/patient/${patientId}/ioc/${encounterId}/program/custom-results`),
                        {
                          state: buildCustomResultsNavigationState(
                            showDownPayment,
                            showRecurringAmount,
                            activeEncounter
                          ),
                        }
                      );
                    } else {
                      ctx.navigate(getProgramPath(`/hub/patient/${patientId}/ioc/${encounterId}/program/${newValue}`));
                    }
                  }
                }}
                aria-label="payment program type"
                sx={{
                  width: '100%',
                  '& .MuiToggleButton-root': {
                    flex: 1,
                    border: '1px solid #e0e0e0',
                    color: '#000',
                    backgroundColor: 'white',
                    '&.Mui-selected': {
                      backgroundColor: '#297ec1',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: '#1f5f8f',
                      },
                    },
                    '&:hover': {
                      backgroundColor: '#f5f5f5',
                    },
                  },
                }}
              >
                <ToggleButton value="full" aria-label="single pay" test-id="payment-program-tab-full">
                  SINGLE PAY
                </ToggleButton>
                {ctx.hasPreauthorizedPermission ? (
                  <ToggleButton value="preauthorized" aria-label="preauthorized payment" test-id="payment-program-tab-preauthorized">
                    Preauthorized
                  </ToggleButton>
                ) : (
                  <ToggleButton value="optimal" aria-label="optimal payment" test-id="payment-program-tab-optimal">
                    Optimal
                  </ToggleButton>
                )}
                <ToggleButton value="custom" aria-label="custom payment" test-id="payment-program-tab-custom">
                  Custom
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          )}

          <Grid container spacing={2.5}>
            <Grid item xs={6}>
              <Typography variant="subtitle1" color="text.secondary">
                Total Estimate
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body1" align="right">
                {formatNumberToUSD(pfrAmt)}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="subtitle1" color="text.secondary">
                Paid to Date
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body1" align="right">
                {formatNumberToUSD(pfrCollected)}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="subtitle1" color="text.secondary">
                Remaining Balance
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body1" align="right">
                {formatNumberToUSD(remainingPfr)}
              </Typography>
            </Grid>

            {/* Billing Frequency toggle (optimal + custom views) */}
            {showBillingFrequencyToggle && (
              <>
                <Grid item xs={6}>
                  <Typography variant="subtitle1" color="text.secondary">
                    Billing Frequency
                  </Typography>
                </Grid>
                <Grid item xs={6} sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                  <ToggleButtonGroup
                    value={billingFrequency}
                    exclusive
                    onChange={(_, newValue: 'monthly' | 'weekly' | null) => {
                      if (newValue !== null) setBillingFrequency(newValue);
                    }}
                    aria-label="billing frequency"
                    size="small"
                    sx={{
                      '& .MuiToggleButton-root': {
                        px: 2,
                        py: 1,
                        border: '1px solid #9e9e9e',
                        color: '#666',
                        backgroundColor: 'white',
                        textTransform: 'none',
                        '&.Mui-selected': {
                          backgroundColor: '#e3f2fd',
                          color: '#1976d2',
                          borderColor: '#1976d2',
                          '&:hover': {
                            backgroundColor: '#bbdefb',
                          },
                        },
                        '&:hover': {
                          backgroundColor: '#f5f5f5',
                        },
                      },
                    }}
                  >
                    <ToggleButton value="monthly" aria-label="monthly" test-id="billing-frequency-monthly">
                      Monthly
                    </ToggleButton>
                    <ToggleButton value="weekly" aria-label="weekly" test-id="billing-frequency-weekly">
                      Weekly
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Grid>
              </>
            )}

            {/* Route view */}
            {currentProgramType === 'full' && <FullPaymentView ctx={ctx} />}
            {currentProgramType === 'preauthorized' && <PreauthorizedPaymentView ctx={ctx} />}
            {currentProgramType === 'optimal' && <OptimalPaymentView ctx={ctx} />}
            {currentProgramType === 'custom' && renderCustomView()}
          </Grid>
        </Paper>
      </div>
    </main>
  );
};

export default PaymentProgramView;
